import { useContext, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppBar, Box, Toolbar, IconButton, Typography, Menu, Button, MenuItem, Avatar } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ShoppingCart } from '@mui/icons-material';
import { UserContext } from '../context/UserProvider';

// Visible pages (Orders, Invoices, and New Order)
const mainPages = [
  { name: 'Orders', url: "/admin/orders", role: "Orders" },
  { name: 'Invoices', url: "/admin/invoices", role: "Admin" },

];

// Full Admin Dropdown Menu (Restored All Pages)
const adminPages = [
  { name: 'Order Guides', url: "/admin/orderGuides", role: "Admin" },
  { name: 'Products', url: "/admin/products", role: "Admin" },
  { name: 'Product Categories', url: "/admin/productCategories", role: "Admin" },
  { name: 'Vendors', url: "/admin/vendors", role: "Admin" },
  { name: 'Facilities', url: "/admin/facilities", role: "Admin" },
  { name: 'Purchasing Groups', url: "/admin/purchasingGroups", role: "Admin" },
  { name: 'Parents', url: "/admin/parents", role: "Admin" },
  { name: 'Users', url: "/admin/users", role: "Admin" },
  { name: 'Site Config', url: "/admin/siteConfig", role: "Admin" },
  { name: 'Quickbooks', url: "/admin/quickbooks", role: "Admin" },
];
const settings = [];

export default function NavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isInRole } = useContext(UserContext);

  const trimedName = user?.firstName?.length && user?.lastName?.length ? user.firstName[0] + user.lastName[0] : user?.firstName?.length > 1 ?
    user.firstName[0] + user.firstName[1] : user?.firstName;

  const [anchorMainNavMenu, setAnchorMainNavMenu] = useState(null);
  const [anchorMiniNavMenu, setAnchorMiniNavMenu] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);

  const handleOpenNavMenu = (event) => setAnchorMainNavMenu(event.currentTarget);
  const handleOpenMiniNavMenu = (event) => setAnchorMiniNavMenu(event.currentTarget);
  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);

  const close = () => {
    setAnchorElUser(null);
    setAnchorMainNavMenu(null);
    setAnchorMiniNavMenu(null);
  }

  const closeAndNavigate = (e, url) => {
    e.preventDefault();
    close();
    if (url) navigate(url)
  }

  const mainVisiblePages = useMemo(() => mainPages.filter(page => isInRole(page.role ?? "Basic-User")), [user]);
  const adminVisiblePages = useMemo(() => adminPages.filter(page => isInRole(page.role ?? "Basic-User")), [user]);

  const currentPage = [...mainVisiblePages, ...adminVisiblePages].find(page => page.url === window.location.pathname);
  const currentAdminPage = adminVisiblePages.find(page => page.url === window.location.pathname);
  const adminLabel = currentAdminPage ? `Admin - ${currentAdminPage.name}` : "Admin";


  const isLoggedIn = !!user;
  const isLandingPage = location.pathname === "/landing";
  if (!isLoggedIn || isLandingPage) return <></>

  return <AppBar position="static" sx={{ px: 2, backgroundColor: "#1976d2" }}>
    <Toolbar disableGutters>
      <Typography
        variant="h5"
        noWrap
        component="a"
        href="/home"
        sx={{
          mr: 2,
          display: { xs: 'none', md: 'flex' },
          color: 'inherit',
        }}
      >
        <img style={{ justifySelf: "center" }} src={"Nhi_logo.png"} alt={"NHI Direct logo"} height={40} width={150} loading="lazy" />
      </Typography>

      <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
        <IconButton size="large" onClick={handleOpenNavMenu} color="inherit">
          <MenuIcon />
        </IconButton>

        {user && <Menu
          anchorEl={anchorMainNavMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          keepMounted
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          open={Boolean(anchorMainNavMenu)}
          onClose={close}
          sx={{ display: { xs: 'block', md: 'none' } }}
        >
          {[...mainVisiblePages, ...adminVisiblePages].map((page) => (
            <MenuItem component="a" href={page.url} key={page.name} onClick={(e) => closeAndNavigate(e, page.url)}>
              <Typography>{page.name}</Typography>
            </MenuItem>
          ))}
        </Menu>}
      </Box>
      <Typography
        variant="h5"
        noWrap
        component="a"
        href="/home"
        sx={{
          mr: 2,
          display: { xs: 'flex', md: 'none' },
          flexGrow: 1,
          color: 'inherit',
        }}
      >
        <img style={{ justifySelf: "center" }} src={"Nhi_logo.png"} alt={"NHI Direct logo"} height={40} width={110} loading="lazy" />
      </Typography>
      {user && <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
        {mainVisiblePages.map(page => (
          <Button
            component="a"
            href={page.url}
            key={page.name}
            onClick={(e) => closeAndNavigate(e, page.url)}
            sx={{
              my: 2,
              color: 'white !important',
              display: 'block',
              fontWeight: currentPage?.url === page.url ? "bold" : "normal",
              textDecoration: currentPage?.url === page.url ? "underline" : "none",
              padding: "5px 10px"
            }}
          >
            {page.name}
          </Button>
        ))}
        {adminVisiblePages?.length > 0 && <Button
          onClick={handleOpenMiniNavMenu}
          sx={{
            my: 2,
            color: 'white !important',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            fontWeight: currentAdminPage ? "bold" : "normal",
            textDecoration: currentAdminPage ? "underline" : "none",
            padding: "5px 10px"
          }}
        >
          {adminLabel}
          <KeyboardArrowDownIcon />
        </Button>}

        <Menu
          anchorEl={anchorMiniNavMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          keepMounted
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          open={Boolean(anchorMiniNavMenu)}
          onClose={close}
          sx={{ display: { xs: 'none', md: 'block' } }}
        >
          {adminVisiblePages.map(page => (
            <MenuItem component="a" href={page.url} key={page.name} onClick={(e) => closeAndNavigate(e, page.url)}>
              <Typography>{page.name}</Typography>
            </MenuItem>
          ))}
        </Menu>
      </Box>}
      <Box sx={{ flexGrow: 0, minWidth: 90 }}>
        {user && isInRole("Orders") && (
          <Button
            href="/admin/orderSheet"
            component="a"
            sx={{
              my: 2,
              mr: 3,
              color: '#1976d2 !important',
              backgroundColor: 'white !important',
              border: '2px solid #1976d2',
              borderRadius: '5px',
              padding: '5px 15px',
              fontWeight: 'bold',
              marginLeft: "auto",
              textTransform: "none",
              "&:hover": {
                backgroundColor: 'white',
                color: '#1976d2',
                border: '2px solid #1976d2',
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.4)"
              }
            }}
          >
            New Order
          </Button>
        )}
        {user && isInRole("Orders") && <IconButton color="primary" href="/admin/cart" sx={{ p: 0, mr: 2 }}>
          <ShoppingCart sx={{ fontSize: 30 }} />
        </IconButton>}
        {user && <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
          <Avatar sx={{ backgroundColor: "purple !important" }}>{`${trimedName}`}</Avatar>
        </IconButton>}
        <Menu
          sx={{ mt: '45px' }}
          anchorEl={anchorElUser}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          keepMounted
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={Boolean(anchorElUser)}
          onClose={close}
        >
          {settings.map((setting) => (
            <MenuItem component="a" href={setting.url} key={setting.name} onClick={(e) => closeAndNavigate(e, setting.url)}>
              <Typography>{setting.name}</Typography>
            </MenuItem>
          ))}
          <MenuItem key={"Logout"} onClick={(e) => { logout(); closeAndNavigate(e, "/landing"); }}>
            <Typography>Logout</Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Toolbar>
  </AppBar>;
}